import styled from "styled-components";
import { Input } from "../../../../components/Input/input";
import { useFormik } from "formik";
import * as yup from "yup";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { StyledButton } from "../../../../components/Button/button";
import ClientAddressSectionHeader from "./client-address-section-header";
import { useEffect, useState } from "react";
import { StyledOutlinedButton } from "../../../../components/Button/button-outlined";
import { StyledSecondaryOutlinedButton } from "../../../../components/Button/button-secondary-outlined";
import { ClientData } from "../../../../services/users/get-logged/types";
import { useMutation } from "@tanstack/react-query";
import { addressVoivodeshipsList } from "../../../../helpers/voievodeshipsList";
import { putAddressUpdate } from "../../../../services/address/update";
import { ToastOptions, toast } from "react-toastify";
import SessionService from "../../../../services/session-service";

interface ClientAddressSectionProps {
  clientData: ClientData;
}
const settings: ToastOptions<{}> = {
  position: "top-center",
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: false,
  draggable: false,
  theme: "light",
};
const ClientAddressSection = ({ clientData }: ClientAddressSectionProps) => {
  const [isEditable, setIsEditable] = useState(false);

  const changeClientAddressMutation = useMutation(putAddressUpdate, {
    onSuccess: (res) => {
      toast.success("Edytowano pomyślnie!", {
        toastId: "EditSuccessfully",
        ...settings,
      });
      const newAddress = {
        dlugosc_geograficzna: clientData.adres_klient.dlugosc_geograficzna,
        id_adres: clientData.adres_klient.id_adres,
        kod_pocztowy: formik.values.postalCode,
        kraj: formik.values.country,
        miejscowosc: formik.values.locality,
        miejscowosc_poczty: formik.values.locality,
        numer_budynku: formik.values.buildingNumber,
        numer_lokalu: formik.values.apartNumber,
        szerokosc_geograficzna: clientData.adres_klient.szerokosc_geograficzna,
        ulica: formik.values.street,
        wojewodztwo: formik.values.voivodship,
      };
      SessionService.setLoggedUserData({
        ...clientData,
        adres_klient: newAddress,
      });
    },
  });
  const postalCodeRegExp = /^[0-9]{2}-[0-9]{3}/;
  const validationSchema = yup.object({
    voivodship: yup.string().required("Województwo jest wymagane"),
    locality: yup
      .string()
      .required("Miejscowość jest wymagana")
      .max(100, "Maksymalnie 100 znaków"),
    postalCode: yup
      .string()
      .required("Kod pocztowy jest wymagany")
      .matches(postalCodeRegExp, "Błędny format!")
      .min(6, "Błędny format!")
      .max(6, "Błędny format!"),
    street: yup
      .string()
      .required("Ulica jest wymagana")
      .max(100, "Maksymalnie 100 znaków"),
    buildingNumber: yup
      .string()
      .required("Numer budynku jest wymagany")
      .max(5, "Maksymalnie 5 znaków"),
    apartNumber: yup
      .number()
      .min(1, "Minimalna wartość to 1")
      .max(9999, "Maksymalna wartość to 9999")
      .typeError("Błędny format!"),
  });
  const formik = useFormik({
    initialValues: {
      voivodship: clientData.adres_klient.wojewodztwo,
      locality: clientData.adres_klient.miejscowosc,
      postalCode: clientData.adres_klient.kod_pocztowy,
      street: clientData.adres_klient.ulica,
      buildingNumber: clientData.adres_klient.numer_budynku,
      apartNumber: clientData.adres_klient.numer_lokalu,
      country: "Polska",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const body = {
        id_magazyn: null,
        id_klient: clientData.id_klient,
        kod_pocztowy: values.postalCode,
        miejscowosc_poczty: values.locality,
        miejscowosc: values.locality,
        ulica: values.street,
        numer_budynku: values.buildingNumber,
        numer_lokalu: values.apartNumber === "" ? null : values.apartNumber,
        wojewodztwo: values.voivodship,
        kraj: "Polska",
      };
      changeClientAddressMutation.mutate(body);
      setIsEditable(false);
    },
  });

  return (
    <ClientAddressForm onSubmit={formik.handleSubmit}>
      <ClientAddressSectionContainer>
        <ClientAddressSectionHeader
          isEditable={isEditable}
          setIsEditable={setIsEditable}
        />
        <Input
          label="Ulica"
          id="street"
          name="street"
          value={formik.values.street}
          onChange={formik.handleChange}
          error={formik.touched.street && Boolean(formik.errors.street)}
          helperText={formik.touched.street && formik.errors.street}
          onBlur={formik.handleBlur}
          disabled={!isEditable}
          variant="outlined"
          sx={{
            width: "100%",
          }}
        />
        <InputsWrapper>
          <Input
            label="Numer budynku"
            id="buildingNumber"
            name="buildingNumber"
            value={formik.values.buildingNumber}
            onChange={formik.handleChange}
            error={
              formik.touched.buildingNumber &&
              Boolean(formik.errors.buildingNumber)
            }
            helperText={
              formik.touched.buildingNumber && formik.errors.buildingNumber
            }
            onBlur={formik.handleBlur}
            disabled={!isEditable}
            variant="outlined"
            sx={{
              width: "47%",
            }}
          />
          <Input
            label="Numer lokalu"
            id="apartNumber"
            name="apartNumber"
            value={formik.values.apartNumber}
            onChange={formik.handleChange}
            error={
              formik.touched.apartNumber && Boolean(formik.errors.apartNumber)
            }
            helperText={formik.touched.apartNumber && formik.errors.apartNumber}
            onBlur={formik.handleBlur}
            disabled={!isEditable}
            variant="outlined"
            sx={{
              width: "47%",
            }}
          />
        </InputsWrapper>
        <InputsWrapper>
          <Input
            label="Kod pocztowy"
            id="postalCode"
            name="postalCode"
            value={formik.values.postalCode}
            onChange={formik.handleChange}
            error={
              formik.touched.postalCode && Boolean(formik.errors.postalCode)
            }
            helperText={formik.touched.postalCode && formik.errors.postalCode}
            onBlur={formik.handleBlur}
            disabled={!isEditable}
            variant="outlined"
            sx={{
              width: "34%",
            }}
          />
          <Input
            label="Miejscowość"
            id="locality"
            name="locality"
            value={formik.values.locality}
            onChange={formik.handleChange}
            error={formik.touched.locality && Boolean(formik.errors.locality)}
            helperText={formik.touched.locality && formik.errors.locality}
            onBlur={formik.handleBlur}
            disabled={!isEditable}
            variant="outlined"
            sx={{
              width: "60%",
            }}
          />
        </InputsWrapper>
        <FormControl fullWidth>
          <InputLabel id="voivodshipLabel">Województwo</InputLabel>
          <Select
            labelId="voivodshipLabel"
            id="voivodship"
            name="voivodship"
            value={formik.values.voivodship}
            label="Województwo"
            onChange={formik.handleChange}
            disabled={!isEditable}
          >
            {addressVoivodeshipsList.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Input
          label="Kraj"
          id="country"
          name="country"
          value={formik.values.country}
          onChange={formik.handleChange}
          error={formik.touched.country && Boolean(formik.errors.country)}
          helperText={formik.touched.country && formik.errors.country}
          onBlur={formik.handleBlur}
          disabled
          variant="outlined"
          sx={{
            width: "100%",
          }}
        />
        {isEditable && (
          <SubmitButtonsWrapper>
            <StyledSecondaryOutlinedButton
              onClick={() => setIsEditable(false)}
              variant="outlined"
              style={{ width: "47%" }}
            >
              Anuluj
            </StyledSecondaryOutlinedButton>
            <StyledButton
              type="submit"
              variant="contained"
              style={{ width: "47%" }}
              disabled={
                !formik.isValid || changeClientAddressMutation.isLoading
              }
            >
              Zatwierdź
            </StyledButton>
          </SubmitButtonsWrapper>
        )}
      </ClientAddressSectionContainer>
    </ClientAddressForm>
  );
};

export default ClientAddressSection;

const ClientAddressSectionContainer = styled.section`
  width: 300px;
  @media (max-width: 430px) {
    width: 270px;
  }
  border-radius: 16px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  justify-content: space-evenly;
  background-color: white;
`;
const ClientAddressForm = styled.form`
  //width: 100%;
  //min-width:260px;
  //max-width:430px;
`;
const InputsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;
const SubmitButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;
// "DANE ADRESOWE"
// "kod_pocztowy": "string",
//         "miejscowosc_poczty": "string",
//         "miejscowosc": "string",
//         "ulica": "string",
//         "numer_budynku": "string",
//         "numer_lokalu": "string",
//         "szerokosc_geograficzna": 0,
//         "dlugosc_geograficzna": 0
